import { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, Pencil, Flame, Archive, RotateCcw, StickyNote, Loader2, Mail, Phone, Send, CalendarClock, AlarmClock } from "lucide-react";
import { toast } from "sonner";

import { AppShell } from "@/components/layout/AppShell";
import { LoadingState, EmptyState, ErrorState } from "@/components/common/StateBlocks";
import { EditContactDialog } from "@/components/crm/EditContactDialog";
import { FollowupItem } from "@/components/followup/FollowupItem";
import { RelatedRunSection } from "@/components/run/RelatedRunSection";
import { crmService, emitCrmChanged } from "@/services/crmService";
import { useCrmActions } from "@/context/CrmActionsContext";
import { relationshipLabel, leadTypeLabel, priorityLabel, temperatureLabel, txStatusLabel } from "@/config/crmOptions";
import { fmtDate, fmtDateTime, priceRange, fmtCents } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";

const ACTIVITY_LABELS = {
  contact_created: "Contact created",
  contact_updated: "Contact updated",
  contact_archived: "Contact archived",
  contact_restored: "Contact restored",
  note_added: "Note added",
  lead_created: "Lead created",
  lead_updated: "Lead updated",
  stage_changed: "Stage changed",
  relationship_changed: "Relationship changed",
  followup_created: "Follow-up scheduled",
  followup_completed: "Follow-up completed",
  followup_rescheduled: "Follow-up rescheduled",
  followup_cancelled: "Follow-up cancelled",
  contact_connected: "Connected with contact",
};

const Detail = ({ label, value }) => (
  <div>
    <p className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">{label}</p>
    <p className="mt-0.5 text-sm text-foreground">{value || "—"}</p>
  </div>
);

export default function ContactProfile() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { openNewLead, openAddFollowup, openNewTransaction } = useCrmActions();
  const [contact, setContact] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [editing, setEditing] = useState(false);
  const [notes, setNotes] = useState([]);
  const [activity, setActivity] = useState([]);
  const [followups, setFollowups] = useState([]);
  const [transactions, setTransactions] = useState([]);
  const [noteBody, setNoteBody] = useState("");
  const [savingNote, setSavingNote] = useState(false);
  const [tab, setTab] = useState("overview");

  const load = useCallback(async () => {
    setLoading(true); setError(false);
    try {
      const c = await crmService.getContact(id);
      setContact(c);
    } catch (e) {
      setError(true);
    } finally {
      setLoading(false);
    }
  }, [id]);

  const loadNotes = useCallback(() => crmService.listNotes(id).then(setNotes).catch(() => {}), [id]);
  const loadActivity = useCallback(() => crmService.contactActivity(id).then(setActivity).catch(() => {}), [id]);
  const loadFollowups = useCallback(() => crmService.listFollowups({ contact_id: id, view: "pending" }).then((d) => setFollowups(d.followups)).catch(() => {}), [id]);
  const loadTransactions = useCallback(() => crmService.listTransactions({ contact_id: id, view: "all", sort: "recent" }).then((d) => setTransactions(d.transactions)).catch(() => {}), [id]);

  useEffect(() => { load(); loadNotes(); loadActivity(); loadFollowups(); loadTransactions(); }, [load, loadNotes, loadActivity, loadFollowups, loadTransactions]);

  useEffect(() => {
    const h = () => { load(); loadNotes(); loadActivity(); loadFollowups(); loadTransactions(); };
    window.addEventListener("crm:changed", h);
    return () => window.removeEventListener("crm:changed", h);
  }, [load, loadNotes, loadActivity, loadFollowups, loadTransactions]);

  const addNote = async () => {
    const body = noteBody.trim();
    if (!body) return;
    setSavingNote(true);
    try {
      await crmService.addNote(id, body);
      setNoteBody("");
      toast.success("Note added");
      loadNotes(); loadActivity(); load();
    } catch (e) {
      toast.error(e.message || "Could not add note");
    } finally {
      setSavingNote(false);
    }
  };

  const archive = async () => {
    try { await crmService.archiveContact(id); toast.success("Contact archived"); emitCrmChanged(); load(); }
    catch { toast.error("Could not archive"); }
  };
  const restore = async () => {
    try { await crmService.restoreContact(id); toast.success("Contact restored"); emitCrmChanged(); load(); }
    catch { toast.error("Could not restore"); }
  };

  if (loading) return <AppShell><div className="py-10"><LoadingState label="Loading contact" /></div></AppShell>;
  if (error || !contact) {
    return <AppShell><ErrorState title="Contact not found" description="This contact may have been removed or is outside your workspace." onRetry={load} /></AppShell>;
  }

  return (
    <AppShell>
      <div className="space-y-6" data-testid="contact-profile-page">
        <button onClick={() => navigate("/crm")} className="flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground" data-testid="contact-back">
          <ArrowLeft className="h-4 w-4" /> Back to CRM
        </button>

        {/* Header */}
        <div className="flex flex-col gap-4 rounded-xl border border-border bg-card p-6 shadow-card sm:flex-row sm:items-center sm:justify-between">
          <div className="min-w-0">
            <div className="flex items-center gap-3">
              <h1 className="truncate font-heading text-2xl font-bold text-foreground" data-testid="contact-name">
                {contact.first_name} {contact.last_name}
              </h1>
              <Badge className="bg-brand-navy text-white hover:bg-brand-navy" data-testid="contact-relationship-badge">
                {relationshipLabel(contact.relationship_type)}
              </Badge>
              {contact.status === "archived" && <Badge variant="outline">Archived</Badge>}
            </div>
            <div className="mt-2 flex flex-wrap items-center gap-4 text-sm text-muted-foreground">
              {contact.email && <span className="flex items-center gap-1.5"><Mail className="h-3.5 w-3.5" /> {contact.email}</span>}
              {contact.phone && <span className="flex items-center gap-1.5"><Phone className="h-3.5 w-3.5" /> {contact.phone}</span>}
              {contact.next_followup_at ? (() => {
                const overdue = new Date(contact.next_followup_at) < new Date();
                return (
                  <span className={`flex items-center gap-1.5 ${overdue ? "font-medium text-red-600" : ""}`} data-testid="contact-next-followup">
                    {overdue ? <AlarmClock className="h-3.5 w-3.5" /> : <CalendarClock className="h-3.5 w-3.5" />}
                    {overdue ? "Overdue: " : "Next follow-up: "}{fmtDateTime(contact.next_followup_at)}
                  </span>
                );
              })() : (
                <span className="flex items-center gap-1.5 text-muted-foreground/70" data-testid="contact-no-followup"><CalendarClock className="h-3.5 w-3.5" /> No follow-up scheduled</span>
              )}
            </div>
          </div>
          <div className="flex flex-wrap gap-2">
            <Button variant="outline" onClick={() => openAddFollowup({ id: contact.id, first_name: contact.first_name, last_name: contact.last_name, email: contact.email }, null, contact.leads || [])} data-testid="contact-add-followup-btn"><CalendarClock className="h-4 w-4" /> Add Follow-Up</Button>
            <Button variant="outline" onClick={() => { setTab("notes"); }} data-testid="contact-add-note-btn"><StickyNote className="h-4 w-4" /> Add Note</Button>
            <Button variant="outline" onClick={() => setEditing(true)} data-testid="contact-edit-btn"><Pencil className="h-4 w-4" /> Edit</Button>
            <Button onClick={() => openNewLead({ id: contact.id, first_name: contact.first_name, last_name: contact.last_name, email: contact.email })} data-testid="contact-new-lead-btn"><Flame className="h-4 w-4" /> New Lead</Button>
            {contact.status === "archived"
              ? <Button variant="ghost" onClick={restore} data-testid="contact-restore-btn"><RotateCcw className="h-4 w-4" /> Restore</Button>
              : <Button variant="ghost" onClick={archive} className="text-destructive" data-testid="contact-archive-btn"><Archive className="h-4 w-4" /> Archive</Button>}
          </div>
        </div>

        <Tabs value={tab} onValueChange={setTab}>
          <TabsList data-testid="contact-tabs">
            <TabsTrigger value="overview" data-testid="tab-overview">Overview</TabsTrigger>
            <TabsTrigger value="activity" data-testid="tab-activity">Activity</TabsTrigger>
            <TabsTrigger value="notes" data-testid="tab-notes">Notes</TabsTrigger>
          </TabsList>

          {/* Overview */}
          <TabsContent value="overview" className="space-y-6 pt-4">
            <Card>
              <CardContent className="grid gap-5 p-6 sm:grid-cols-2 lg:grid-cols-3">
                <Detail label="Relationship" value={relationshipLabel(contact.relationship_type)} />
                <Detail label="Lead source" value={contact.lead_source?.name} />
                <Detail label="Preferred contact" value={contact.preferred_contact_method} />
                <Detail label="Last contact" value={fmtDate(contact.last_contact_at)} />
                <Detail label="Next follow-up" value={fmtDate(contact.next_followup_at)} />
                <Detail label="Birthday" value={fmtDate(contact.birthday)} />
                <Detail label="Address" value={[contact.address_line1, contact.address_line2, [contact.city, contact.state].filter(Boolean).join(", "), contact.postal_code].filter(Boolean).join(" · ")} />
              </CardContent>
            </Card>

            {contact.notes_summary && (
              <Card><CardContent className="p-6"><Detail label="Summary" value={contact.notes_summary} /></CardContent></Card>
            )}

            {(contact.tags || []).length > 0 && (
              <div className="flex flex-wrap items-center gap-2">
                <span className="text-sm text-muted-foreground">Tags:</span>
                {contact.tags.map((t) => <Badge key={t.id} variant="secondary">{t.name}</Badge>)}
              </div>
            )}

            {/* Upcoming follow-ups */}
            <div>
              <div className="mb-3 flex items-center justify-between">
                <h3 className="text-sm font-bold text-foreground">Upcoming follow-ups</h3>
                <Button size="sm" variant="outline" onClick={() => openAddFollowup({ id: contact.id, first_name: contact.first_name, last_name: contact.last_name, email: contact.email }, null, contact.leads || [])} data-testid="contact-overview-add-followup">
                  <CalendarClock className="h-3.5 w-3.5" /> Add Follow-Up
                </Button>
              </div>
              {followups.length === 0 ? (
                <EmptyState title="No follow-ups scheduled" description="Schedule a call, email, or check-in so this relationship stays warm." />
              ) : (
                <div className="space-y-2" data-testid="contact-followups">
                  {followups.map((f) => <FollowupItem key={f.id} followup={f} onChanged={() => { load(); loadFollowups(); loadActivity(); }} />)}
                </div>
              )}
            </div>

            {/* Transactions for this contact */}
            <div>
              <div className="mb-3 flex items-center justify-between">
                <h3 className="text-sm font-bold text-foreground">Transactions</h3>
                <Button size="sm" variant="outline" onClick={() => openNewTransaction({ id: contact.id, first_name: contact.first_name, last_name: contact.last_name, email: contact.email })} data-testid="contact-new-transaction">
                  <CalendarClock className="h-3.5 w-3.5" /> New Transaction
                </Button>
              </div>
              {transactions.length === 0 ? (
                <EmptyState title="No transactions yet" description="Start a transaction when this client moves into an active deal." />
              ) : (
                <div className="space-y-2" data-testid="contact-transactions">
                  {transactions.map((t) => (
                    <div key={t.id} className="flex flex-wrap items-center justify-between gap-2 rounded-lg border border-border bg-card px-4 py-3 cursor-pointer hover:shadow-card" onClick={() => navigate(`/transactions/${t.id}`)} data-testid={`contact-tx-${t.id}`}>
                      <div>
                        <p className="text-sm font-medium text-foreground">{t.property_address_line1}</p>
                        <p className="text-xs text-muted-foreground">{txStatusLabel(t.status)} · Closing {fmtDate(t.expected_closing_date)}{t.commission ? ` · Net ${fmtCents(t.commission.estimated_agent_net)}` : ""}</p>
                      </div>
                      <Badge variant="outline">{fmtCents(t.sale_price)}</Badge>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Tasks + Documents for this contact */}
            <RelatedRunSection entityType="contact" entityId={contact.id}
              related={{ type: "contact", id: contact.id, contact_id: contact.id, label: `${contact.first_name} ${contact.last_name}` }} />

            {/* Leads for this contact */}
            <div>
              <h3 className="mb-3 text-sm font-bold text-foreground">Leads</h3>
              {(contact.leads || []).length === 0 ? (
                <EmptyState title="No leads yet" description="Create a lead to start tracking this opportunity."
                  action={<Button className="mt-4" onClick={() => openNewLead({ id: contact.id, first_name: contact.first_name, last_name: contact.last_name, email: contact.email })} data-testid="contact-overview-new-lead"><Flame className="h-4 w-4" /> New Lead</Button>} />
              ) : (
                <div className="space-y-2" data-testid="contact-leads">
                  {contact.leads.map((l) => (
                    <div key={l.id} className="flex flex-wrap items-center justify-between gap-2 rounded-lg border border-border bg-card px-4 py-3">
                      <div>
                        <p className="text-sm font-medium text-foreground">{leadTypeLabel(l.lead_type)} lead · {l.stage?.name || "—"}</p>
                        <p className="text-xs text-muted-foreground">
                          {priceRange(l.price_min, l.price_max) || "No budget set"}{l.desired_location ? ` · ${l.desired_location}` : ""}
                        </p>
                      </div>
                      <div className="flex items-center gap-2">
                        <Badge variant="outline">{priorityLabel(l.priority)}</Badge>
                        <Badge variant="outline">{temperatureLabel(l.temperature)}</Badge>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </TabsContent>

          {/* Activity */}
          <TabsContent value="activity" className="pt-4">
            {activity.length === 0 ? (
              <EmptyState title="No activity yet" description="Actions on this contact will appear here." />
            ) : (
              <div className="space-y-3" data-testid="contact-activity-timeline">
                {activity.map((a) => (
                  <div key={a.id} className="flex gap-3 rounded-lg border border-border bg-card px-4 py-3">
                    <div className="mt-1 h-2 w-2 shrink-0 rounded-full bg-brand-gold" />
                    <div className="min-w-0">
                      <p className="text-sm font-medium text-foreground">{ACTIVITY_LABELS[a.type] || a.type}</p>
                      {a.meta?.to && <p className="text-xs text-muted-foreground">{a.meta.from ? `${a.meta.from} → ` : ""}{a.meta.to}</p>}
                      {a.meta?.preview && <p className="truncate text-xs text-muted-foreground">“{a.meta.preview}”</p>}
                      <p className="mt-0.5 text-xs text-muted-foreground">{a.actor_name} · {fmtDateTime(a.created_at)}</p>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </TabsContent>

          {/* Notes */}
          <TabsContent value="notes" className="space-y-4 pt-4">
            <div className="rounded-xl border border-border bg-card p-4 shadow-card">
              <Textarea rows={3} value={noteBody} onChange={(e) => setNoteBody(e.target.value)} placeholder="Add a note about this contact…" data-testid="note-input" />
              <div className="mt-3 flex justify-end">
                <Button onClick={addNote} disabled={savingNote || !noteBody.trim()} data-testid="note-save">
                  {savingNote ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Send className="h-4 w-4" /> Add Note</>}
                </Button>
              </div>
            </div>
            {notes.length === 0 ? (
              <EmptyState title="No notes yet" description="Notes you add are kept as a chronological history." />
            ) : (
              <div className="space-y-3" data-testid="note-list">
                {notes.map((n) => (
                  <div key={n.id} className="rounded-lg border border-border bg-card px-4 py-3">
                    <p className="whitespace-pre-wrap text-sm text-foreground">{n.body}</p>
                    <p className="mt-2 text-xs text-muted-foreground">{n.author_name} · {fmtDateTime(n.created_at)}</p>
                  </div>
                ))}
              </div>
            )}
          </TabsContent>
        </Tabs>
      </div>

      <EditContactDialog open={editing} onOpenChange={setEditing} contact={contact} onSaved={() => { setEditing(false); load(); }} />
    </AppShell>
  );
}
